import { useState, useMemo } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingCart,
  faPlus,
  faEye,
  faEdit,
  faStore,
  faSpinner,
  faArrowUp,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";
import DateFilter from "../../../components/ui/DateFilter";
import { AddSaleModal } from "./AddSaleModal";
import EditSaleModal from "./EditSaleModal";
import SaleDetailModal from "./SaleDetailModal";
import {
  useSales,
  useSaleAvailableStock,
  useShopSellers,
  useShopStocks,
} from "../../../hooks";
import { useAuth } from "../../../hooks";

const SalesTab = ({ shop }) => {
  const { hasPermission } = useAuth();
  const canManageSales = hasPermission(["manage_sales", "manage_shop_modules"]);

  const shopId = shop?.shopId || shop?.id;

  const {
    sales,
    pagination,
    loading: salesLoading,
    error: salesError,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    refresh: refreshSales,
    createSales,
    updateSale,
  } = useSales({ shopId });

  const { sellers, loading: sellersLoading } = useShopSellers(shopId, {
    per_page: 10,
  });

  const { stocks, loading: stocksLoading } = useShopStocks(shopId, {
    per_page: 10,
  });

  const { stocks: availableSaleStocks, loading: availableSaleStocksLoading } =
    useSaleAvailableStock(shopId);

  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  const stats = useMemo(
    () =>
      sales.reduce(
        (totals, sale) => {
          totals.total_sales += 1;
          totals.total_revenue += Number(sale.actualTotalRevenue || 0);
          totals.total_profit += Number(sale.actualProfit || 0);
          return totals;
        },
        { total_sales: 0, total_revenue: 0, total_profit: 0 },
      ),
    [sales],
  );

  const formatCurrency = (amount) => {
    if (!amount) return "0 TSH";
    return (
      new Intl.NumberFormat("sw-TZ", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount) + " TSH"
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getActualRevenue = (sale) => Number(sale.actualTotalRevenue ?? 0);

  const handleAddSale = async (saleItems) => {
    const payloads = saleItems.map((item) => ({
      sellerId: item.sellerId,
      stockId: item.stockId,
      selling_price: item.sellingPrice,
      no_of_unsold: item.noOfUnSold,
      status: item.status?.toLowerCase(),
    }));
    await createSales(payloads);
  };

  const handleUpdateSale = async (saleData) => {
    try {
      const id = saleData.salesId || saleData.id;
      await updateSale(id, saleData);
      setIsEditModalOpen(false);
      setSelectedSale(null);
    } catch (error) {
      console.error("Error updating sale:", error);
    }
  };

  const MobileSaleCard = ({ sale, index }) => {
    const revenue = getActualRevenue(sale);

    return (
      <div className="bg-white border-2 border-blue-900 rounded-md p-4 shadow-sm transition-all duration-200">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-white font-medium bg-blue-900 px-2 rounded-full">
                {index + 1}
              </span>
              <h4 className="text-sm font-semibold text-gray-900 truncate">
                {sale.productName}
              </h4>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1.5">
              <FontAwesomeIcon
                icon={faStore}
                className="h-3 w-3 text-gray-600"
              />
              <span className="font-medium text-sm">Category:</span>
              <span className="text-orange-500 font-medium text-sm">
                {sale.productCategory}
              </span>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="font-medium text-sm">Date:</span>
              <span className="font-medium text-sm text-blue-900">
                {formatDate(sale.saleDate)}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-1.5 pt-1.5 border-t border-gray-100">
              <div className="flex items-center gap-1 text-xs text-gray-600">
                <FontAwesomeIcon
                  icon={faStore}
                  className="h-3 w-3 text-gray-600"
                />
                <span className="text-gray-700 text-sm truncate max-w-[120px]">
                  {sale.shopName}
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <span className="text-gray-800 text-sm">Revenue:</span>
                <span className="font-semibold text-sm text-green-600">
                  {formatCurrency(revenue)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedSale(sale);
                setIsViewModalOpen(true);
              }}
              title="View Sale"
              className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50 px-2.5 py-1 h-8 text-xs"
            >
              <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
              <span className="hidden xs:inline">View</span>
            </Button>
            {canManageSales && (
              <Button
                size="sm"
                title="Edit Sale"
                variant="outline"
                onClick={() => {
                  setSelectedSale(sale);
                  setIsEditModalOpen(true);
                }}
                className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50 px-2.5 py-1 h-8 text-xs"
              >
                <FontAwesomeIcon icon={faEdit} className="h-3 w-3" />
                <span className="hidden xs:inline">Edit</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (
    salesLoading ||
    sellersLoading ||
    stocksLoading ||
    availableSaleStocksLoading
  ) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-8 w-8 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading sales data...</p>
        </div>
      </div>
    );
  }

  if (salesError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md">
          <h3 className="text-red-700 font-semibold">Error loading sales</h3>
          <p className="text-sm text-red-600 mt-1">
            {salesError?.message || "Something went wrong"}
          </p>
          <Button
            className="mt-3 bg-red-600 hover:bg-red-700 text-white"
            onClick={refreshSales}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;

  return (
    <div className="flex flex-col gap-4 sm:gap-6 max-w-8xl mx-auto px-3 sm:px-4 lg:px-0">
      <Card className="bg-white border border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faShoppingCart}
                className="text-blue-800 h-4 w-4 sm:h-5 sm:w-5"
              />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
                Sales Transactions
              </h2>
              <p className="text-sm sm:text-sm text-gray-600 mt-0.5">
                Track and manage all sales for this shop
              </p>
            </div>
          </div>
          {canManageSales && (
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Sale
            </Button>
          )}
        </div>
      </Card>

      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={() => {
          setIsFilterApplied(true);
          setCurrentPage(1);
        }}
        onClear={() => {
          setStartDate("");
          setEndDate("");
          setIsFilterApplied(false);
          setCurrentPage(1);
        }}
        isFilterApplied={isFilterApplied}
        totalItems={totalItems}
        label="sales"
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faShoppingCart}
                className="text-blue-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Total Sales</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
            {stats.total_sales || 0}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faArrowUp}
                className="text-green-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Revenue</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600">
            {formatCurrency(stats.total_revenue || 0)}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center col-span-2 sm:col-span-1">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faArrowUp}
                className="text-orange-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Profit</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-600">
            {formatCurrency(stats.total_profit || 0)}
          </p>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          placeholder="Search by product, category, type, seller"
          className="flex-1"
        />

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-44 h-9 sm:h-10 border border-gray-300 rounded-lg bg-white text-sm sm:text-sm">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faStore}
                  className="text-gray-600 h-3 w-3"
                />
                <SelectValue placeholder="Status" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="hidden md:block">
        <TableWrapper
          title="sales records"
          totalItems={totalItems}
          currentPage={currentPage}
          totalPages={totalPages}
          className="bg-white border border-gray-200 shadow-sm"
        >
          <table className="w-full text-sm sm:text-sm">
            <thead>
              <tr className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <th className="text-left text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  #
                </th>
                <th className="text-left text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                  Product
                </th>
                <th className="text-left text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden lg:table-cell">
                  Seller
                </th>
                <th className="text-right text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden sm:table-cell">
                  Revenue
                </th>
                <th className="text-left text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden sm:table-cell">
                  Date
                </th>
                <th className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Status
                </th>
                <th className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sales.length > 0 ? (
                sales.map((sale, i) => {
                  const globalIndex = (currentPage - 1) * pageSize + i + 1;
                  const revenue = getActualRevenue(sale);

                  return (
                    <tr
                      key={sale.salesId}
                      className="hover:bg-blue-50/50 transition-colors"
                    >
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 font-medium">
                        {globalIndex}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-800 hidden md:table-cell">
                        <div>
                          <p className="font-medium text-gray-900">
                            {sale.productName}
                          </p>
                          <p className="text-[12px] sm:text-xs text-gray-600">
                            {sale.productCategory}
                          </p>
                        </div>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 hidden lg:table-cell">
                        <p className="font-medium">{sale.sellerName}</p>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-right text-sm font-semibold text-green-600 hidden sm:table-cell">
                        {formatCurrency(revenue)}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 hidden sm:table-cell">
                        {formatDate(sale.saleDate)}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-center">
                        <Badge
                          variant="outline"
                          className="bg-green-50 text-green-700 border-green-200 border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5 justify-center"
                        >
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="h-3 w-3"
                          />
                          {sale.status}
                        </Badge>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4">
                        <div className="flex gap-1 sm:gap-2 justify-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-blue-600 hover:bg-blue-50"
                            onClick={() => {
                              setSelectedSale(sale);
                              setIsViewModalOpen(true);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faEye}
                              className="h-3 w-3 sm:h-4 sm:w-4"
                            />
                          </Button>
                          {canManageSales && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-green-600 hover:bg-green-50"
                              onClick={() => {
                                setSelectedSale(sale);
                                setIsEditModalOpen(true);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="h-3 w-3 sm:h-4 sm:w-4"
                              />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-6 sm:py-8">
                    <EmptyState
                      title="No sales found"
                      message={
                        searchTerm || statusFilter !== "all" || isFilterApplied
                          ? "Try adjusting your search or filters"
                          : "Add your first sale to get started"
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableWrapper>
      </div>

      <div className="md:hidden">
        {sales.length > 0 ? (
          <>
            <div className="flex items-center justify-between text-xs text-gray-600 mb-3 px-1">
              <span>
                Showing {(currentPage - 1) * pageSize + 1}-
                {Math.min(currentPage * pageSize, totalItems)} of {totalItems}{" "}
                sales
              </span>
              <span>
                Page {currentPage} of {totalPages}
              </span>
            </div>
            <div className="space-y-3">
              {sales.map((sale, i) => (
                <MobileSaleCard key={sale.salesId} sale={sale} index={i} />
              ))}
            </div>
          </>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <EmptyState
              title="No sales found"
              message={
                searchTerm || statusFilter !== "all" || isFilterApplied
                  ? "Try adjusting your search or filters"
                  : "Add your first sale to get started"
              }
            />
          </div>
        )}
      </div>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {isAddModalOpen && (
        <AddSaleModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddSale}
          sellers={sellers}
          stockData={availableSaleStocks}
        />
      )}

      {isEditModalOpen && selectedSale && (
        <EditSaleModal
          sale={selectedSale}
          onUpdate={handleUpdateSale}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedSale(null);
          }}
          sellers={sellers}
          stockData={stocks}
        />
      )}

      {isViewModalOpen && selectedSale && (
        <>
          {console.log("=== SALE PASSED TO MODAL ===", selectedSale)}
          <SaleDetailModal
            sale={selectedSale}
            onClose={() => {
              setIsViewModalOpen(false);
              setSelectedSale(null);
            }}
            onEdit={(sale) => {
              if (!canManageSales) return;
              setIsViewModalOpen(false);
              setSelectedSale(sale);
              setIsEditModalOpen(true);
            }}
          />
        </>
      )}
    </div>
  );
};

export default SalesTab;
